import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Flex, Box, Text, Image } from 'rebass/styled-components'
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { useCollapse } from 'react-collapsed';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_APPOINTMENTS_AND_TICKLERS, GET_APPOINTMENT_NOTES, GET_APPOINTMENT_SUMMARY, GET_INTAKE_STATUS } from '../../graphql/queries';
import { IAppointment, IGetAppointmentNotesInput, IGetAppointmentNotesOutput, IGetAppointmentsAndTicklersInput, IGetAppointmentsAndTicklersOutput, IGetIntakeStatusInput, IGetIntakeStatusOutput, IProviderGetAppointmentSummaryInput, IProviderGetAppointmentSummaryOutput, ITickler } from '../../graphql/types/queries/appointment';
import moment from 'moment';
import PleaseWait from '../../components/PleaseWait';
import doctorIcon from '../../static/doctorIcon.png';
import { Skeleton } from '../../components/loadingSkeleton/Skeleton';
import SwitchSelector from 'react-switch-selector';
import { PatientCases } from './PatientCases';
import Select, { components } from "react-select";
import { ReactSelectStyles } from '../../styles/ReactSelectStyles';
import { RbCircledChecked, RbCircledUnchecked } from '../../components/RadioButton';
import { useIsVisible } from '../../hooks/useIsVisible';

interface IAppointmentsProps {
  id: string
  isMinimized?: boolean
  defaultSelectedPeriod?:'upcoming' | 'past' | 'cases'
}
const ListContainer = styled(Flex)`
  /* border-right: 1px solid #ccc; */
  width: 100%;
  /* min-width: 200px; */
  overflow-y: auto;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
  /* border-right: 1px solid #355078; */
  &.selected {
    display: none;
  }
`;
const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <Flex alignItems={'center'}>
        {props.isSelected ? (
          <RbCircledChecked />
        ) : <RbCircledUnchecked />}

        <Text ml={2}>{props.children}</Text>
      </Flex>
    </components.Option>
  );
};

const iconStyle = {
  width: '20px',
  height: '20px',
  background: '#FFFFFF',
  border: '3px solid #BCFFEB',
  margin: '10px',
  boxShadow: 'initial'
}

const contentStyle = {
  background: '#F0F5FD',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
  borderRadius: '10px',
  padding: '12px 0px',
}
const contentStyleMinimized = {
  background: '#F0F5FD',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
  borderRadius: '10px',
  padding: '12px 0px',
  marginBottom: '12px'
}
const contentArrowStyle = {
  borderRight: '7px solid  #F0F5FD',
}


const SelectorLarge = styled(Flex)`
  display: none;
  @media screen and (min-width: 1740px) {
    display: initial;
  }
`

const SelectorSmall = styled(Flex)`
  display: none;
  @media screen and (max-width: 1739px) {
    display: initial;
  }
`

const AppointmentDetails = ({ data, isPast }: { data: IAppointmentExtended, isPast: boolean }) => {

  const { loading, error, data: dataSummary } = useQuery<IProviderGetAppointmentSummaryOutput, IProviderGetAppointmentSummaryInput>(GET_APPOINTMENT_SUMMARY, {
    variables: {
      appointmentId: data.id!
    }
  })
  const { loading: loadingNotes, error: errorNotes, data: dataNotes } = useQuery<IGetAppointmentNotesOutput, IGetAppointmentNotesInput>(GET_APPOINTMENT_NOTES, {
    variables: {
      appointmentId: data.id!
    }
  })

  return (
    <Flex width={'100%'} flexDirection='column' mt={3} padding='0 16px'>
      {!isPast && (
        <>
          {!!loadingNotes ? (<Flex flex={1} flexDirection={'column'} justifyContent='center' alignItems={'center'} >
            <PleaseWait
              text={` `} />
          </Flex>) : !dataNotes?.healthCoachGetAppointmentNotes || dataNotes.healthCoachGetAppointmentNotes.length < 1 ? (
            <>
              <Text fontSize='12px' mt={3} mb={4}>No Appointment Notes</Text>
            </>
          ) : (
            <>
              <Text fontSize='18px'>Appointment Notes:</Text>
              {dataNotes.healthCoachGetAppointmentNotes.map(note => {
                return (<Text ml={3} fontSize='12px'>{`• ${note.noteText}`}</Text>)
              })}
            </>
          )}
        </>
      )}
      {!!isPast && (
        <>
          {!!loading ? (<Flex flex={1} flexDirection={'column'} justifyContent='center' alignItems={'center'} >
            <PleaseWait
              text={` `} />
          </Flex>) : !dataSummary?.providerGetAppointmentSummary ? (
            <Text fontSize='12px' mt={3}>No Summary Information</Text>
          ) : ((<Flex flexDirection={'row'}>
            <Flex flexDirection={'column'}>
              <Flex alignItems={'center'} mt={3}>
                <Image src={doctorIcon} width='32px' height={'32px'} mr={3} />
                <Text fontSize='18px'>Visit Summary</Text>
              </Flex>
              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={3}>Patient instructions</Text>
              <Text paddingY={'8px'} ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.instructions || ''
              }}></Text>

              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={4}>Patient goals</Text>
              <Text paddingY={'8px'} ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.goals || ''
              }} />


              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={4}>Discussion notes</Text>
              <Text paddingY={'8px'} ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.discussionNotes || ''
              }} />

              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={4}>Assessment</Text>
              <Text paddingY={'8px'} ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.assessment || ''
              }} />

              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={4}>Diagnoses</Text>
              <Text paddingY={'8px'} ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.diagnoses || ''
              }} />

              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={4}>Prescription</Text>
              <Box ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid red'
              }}>
                {dataSummary.providerGetAppointmentSummary.prescription?.map(
                  x => {
                    return (
                      <Flex flexDirection={'column'}>
                        <Text fontWeight={500} fontSize='12px' mt={'8px'}>{x.description}</Text>
                        <Text fontWeight={300} fontSize='12px' >{x.dosageQuantity} {x.quantityValue}</Text>
                        <Text fontWeight={300} fontSize='12px' >{x.instructions}</Text>

                      </Flex>
                    )
                  }
                )}
              </Box>
              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={3}>HPI</Text>
              <Text paddingY={'8px'} ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' dangerouslySetInnerHTML={{
                __html: (dataSummary.providerGetAppointmentSummary.hpi || '').replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '<br />')
              }}></Text>
              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={3}>Physical Exam</Text>
              <Text paddingY={'8px'} ml={['12px', '62px', '50px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' dangerouslySetInnerHTML={{
                __html: (dataSummary.providerGetAppointmentSummary.physicalExam || '').replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '<br />')
              }}></Text>


            </Flex>
          </Flex>))}
        </>
      )}
    </Flex>
  )
}
const AppointmentDetailsMinimized = ({ data, isPast }: { data: IAppointmentExtended, isPast: boolean }) => {

  const { loading, error, data: dataSummary } = useQuery<IProviderGetAppointmentSummaryOutput, IProviderGetAppointmentSummaryInput>(GET_APPOINTMENT_SUMMARY, {
    variables: {
      appointmentId: data.id!
    }
  })

  const { loading: loadingNotes, error: errorNotes, data: dataNotes } = useQuery<IGetAppointmentNotesOutput, IGetAppointmentNotesInput>(GET_APPOINTMENT_NOTES, {
    variables: {
      appointmentId: data.id!
    }
  })

  return (
    <Flex width={'100%'} flexDirection='column' mt={3} padding='0 16px'>
      {!isPast && (
        <>
          {!!loadingNotes ? (<Flex flex={1} flexDirection={'column'} justifyContent='center' alignItems={'center'} >
            <PleaseWait
              text={` `} />
          </Flex>) : !dataNotes?.healthCoachGetAppointmentNotes || dataNotes.healthCoachGetAppointmentNotes.length < 1 ? (
            <>
              <Text fontSize='12px' mt={3} mb={4}>No Appointment Notes</Text>
            </>
          ) : (
            <>
              <Text fontSize='18px'>Appointment Notes:</Text>
              {dataNotes.healthCoachGetAppointmentNotes.map(note => {
                return (<Text ml={3} fontSize='12px'>{`• ${note.noteText}`}</Text>)
              })}
            </>
          )}
        </>
      )}

      {!!isPast && (
        <>
          {!!loading ? (<Flex flex={1} flexDirection={'column'} justifyContent='center' alignItems={'center'} >
            <PleaseWait
              text={` `} />
          </Flex>) : !dataSummary?.providerGetAppointmentSummary ? (
            <Text fontSize='12px' mt={3}>No Summary Information</Text>
          ) : ((<Flex flexDirection={'row'}>
            <Flex flexDirection={'column'}>
              <Flex alignItems={'center'} mt={3}>
                <Image src={doctorIcon} width='32px' height={'32px'} mr={3} />
                <Text fontSize='18px'>Visit Summary</Text>
              </Flex>
              <Text ml={['8px']} fontSize='14px' fontWeight={500} mt={3}>Patient instructions</Text>
              <Text paddingY={'8px'} ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.instructions || ''
              }}></Text>

              <Text ml={['8px']} fontSize='14px' fontWeight={500} mt={4}>Patient goals</Text>
              <Text paddingY={'8px'} ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.goals || ''
              }} />


              <Text ml={['8px', '50px', '50px']} fontSize='14px' fontWeight={500} mt={4}>Discussion notes</Text>
              <Text paddingY={'8px'} ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.discussionNotes || ''
              }} />

              <Text ml={['8px']} fontSize='14px' fontWeight={500} mt={4}>Assessment</Text>
              <Text paddingY={'8px'} ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.assessment || ''
              }} />

              <Text ml={['8px']} fontSize='14px' fontWeight={500} mt={4}>Diagnoses</Text>
              <Text paddingY={'8px'} ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' mt={3} dangerouslySetInnerHTML={{
                __html: dataSummary.providerGetAppointmentSummary.diagnoses || ''
              }} />

              <Text ml={['8px']} fontSize='14px' fontWeight={500} mt={4}>Prescription</Text>
              <Box ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid red'
              }}>
                {dataSummary.providerGetAppointmentSummary.prescription?.map(
                  x => {
                    return (
                      <Flex flexDirection={'column'}>
                        <Text fontWeight={500} fontSize='12px' mt={'8px'}>{x.description}</Text>
                        <Text fontWeight={300} fontSize='12px' >{x.dosageQuantity} {x.quantityValue}</Text>
                        <Text fontWeight={300} fontSize='12px' >{x.instructions}</Text>

                      </Flex>
                    )
                  }
                )}
              </Box>
              <Text ml={['8px']} fontSize='14px' fontWeight={500} mt={3}>HPI</Text>
              <Text paddingY={'8px'} ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' dangerouslySetInnerHTML={{
                __html: (dataSummary.providerGetAppointmentSummary.hpi || '').replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '<br />')
              }}></Text>
              <Text ml={['8px']} fontSize='14px' fontWeight={500} mt={3}>Physical Exam</Text>
              <Text paddingY={'8px'} ml={['12px']} style={{
                paddingLeft: '12px',
                borderLeft: '1px solid #344F7A99'
              }} fontWeight={300} fontSize='12px' dangerouslySetInnerHTML={{
                __html: (dataSummary.providerGetAppointmentSummary.physicalExam || '').replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '<br />')
              }}></Text>


            </Flex>
          </Flex>))}
        </>
      )}
    </Flex>
  )
}

const AppointmentDisplay = ({ data, isPast }: { data: IAppointmentExtended, isPast: boolean }) => {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse()

  return (
    <Flex flexDirection={'column'} width='100%'>
      <Flex flexDirection={['column', 'row', 'row']}>
        <Flex flexDirection={['row', 'column', 'column']} width={['100%', '120px', '120px']} paddingX={['12px', 0, 0]} justifyContent={['flex-start', 'center', 'center']} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={400} fontSize='14px'>{`${data.date}`}</Text>
            <Text fontWeight={300} fontSize='12px'>{moment(data.startTime, 'HH:mm').format('hh:mmA')}-{moment(data.startTime, 'HH:mm').add(data.duration || 0, 'minutes').format('hh:mmA')}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none', 'initial', 'initial']} />
        <Flex mt={[2, 0, 0]} flexDirection={'column'} width={['100%', '180px', '180px']} paddingX={['12px', 0, 0]} justifyContent={['flex-start', 'center', 'center']} alignItems={['flex-start', 'center', 'center']}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Appointment type</Text>
            <Text fontWeight={300} fontSize='14px'>{data.appointmentType}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none', 'initial', 'initial']} />
        <Flex mt={[2, 0, 0]} flexDirection={'column'} flex={1} justifyContent={['flex-start', 'center', 'center']} alignItems='flex-start' paddingX={'12px'}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Provider</Text>
            <Text fontWeight={300} fontSize='14px'>{data.provider.displayName}</Text>
          </Flex>
        </Flex>
        {data.id && (
          <Flex {...getToggleProps()} style={{ alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} mt={3} mb={3} mr={'18px'}>
            <Text mr={2} fontWeight={300} fontSize='12px' color='#2196F3'>{!isExpanded ? 'Expand' : 'Collapse'}</Text>
            {!isExpanded ? (
              <svg width="10" height="10" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.4375 5.34375L12.4688 4.375L7.5 9.34375L2.53125 4.375L1.5625 5.34375L7.5 11.25L13.4375 5.34375Z" fill="#2196F3" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5625 9.65625L2.53125 10.625L7.5 5.65625L12.4687 10.625L13.4375 9.65625L7.5 3.75L1.5625 9.65625Z" fill="#2196F3" />
              </svg>

            )}

          </Flex>
        )}
      </Flex>
      <div  {...getCollapseProps()}>
        {!!data.id && !!isExpanded && <AppointmentDetails data={data} isPast={isPast} />}
      </div>
    </Flex>
  )
}

const AppointmentDisplayMinimized = ({ data, isPast }: { data: IAppointmentExtended, isPast: boolean }) => {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse()


  return (
    <Flex flexDirection={'column'} width='100%'>
      <Flex flexDirection={['column']}>
        <Flex flexDirection={['row']} width={['100%']} paddingX={['12px']} justifyContent={['flex-start']} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={400} fontSize='14px'>{`${data.date}`}</Text>
            <Text fontWeight={300} fontSize='12px'>{moment(data.startTime, 'HH:mm').format('hh:mmA')}-{moment(data.startTime, 'HH:mm').add(data.duration || 0, 'minutes').format('hh:mmA')}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none']} />
        <Flex mt={[2]} flexDirection={'column'} width={['100%']} paddingX={['12px']} justifyContent={['flex-start']} alignItems={['flex-start']}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Appointment type</Text>
            <Text fontWeight={300} fontSize='14px'>{data.appointmentType}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none']} />
        <Flex mt={[2, 0, 0]} flexDirection={'column'} flex={1} justifyContent={['flex-start']} alignItems='flex-start' paddingX={'12px'}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Provider</Text>
            <Text fontWeight={300} fontSize='14px'>{data.provider.displayName}</Text>
          </Flex>
        </Flex>
        {data.id && (
          <Flex {...getToggleProps()} style={{ alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} mt={3} mb={3} mr={'18px'}>
            <Text mr={2} fontWeight={300} fontSize='12px' color='#2196F3'>{!isExpanded ? 'Expand' : 'Collapse'}</Text>
            {!isExpanded ? (
              <svg width="10" height="10" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.4375 5.34375L12.4688 4.375L7.5 9.34375L2.53125 4.375L1.5625 5.34375L7.5 11.25L13.4375 5.34375Z" fill="#2196F3" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5625 9.65625L2.53125 10.625L7.5 5.65625L12.4687 10.625L13.4375 9.65625L7.5 3.75L1.5625 9.65625Z" fill="#2196F3" />
              </svg>

            )}

          </Flex>
        )}
      </Flex>
      <div  {...getCollapseProps()}>
        {!!data.id && !!isExpanded && <AppointmentDetailsMinimized data={data} isPast={isPast} />}
      </div>
    </Flex>
  )
}

const AppointmentDisplayLoading = ({ isPast }: { isPast: boolean }) => {
  return (
    <Flex flexDirection={'column'} width='100%'>
      <Flex flexDirection={['column', 'row', 'row']}>
        <Flex flexDirection={'column'} width='120px' justifyContent={'center'} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={400} fontSize='14px'><Skeleton height={19} width={90} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
            <Text fontWeight={300} fontSize='12px'><Skeleton height={16} width={100} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} />
        <Flex flexDirection={'column'} width='180px' justifyContent={'center'} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'><Skeleton height={16} width={112} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
            <Text fontWeight={300} fontSize='14px'><Skeleton height={19} width={112} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} />
        <Flex flexDirection={'column'} flex={1} justifyContent={'center'} alignItems='flex-start' paddingX={'12px'}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'><Skeleton height={16} width={75} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
            <Text fontWeight={300} fontSize='14px'><Skeleton height={19} width={163} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
          </Flex>
        </Flex>
        {!!isPast && (
          <Skeleton height={18} width={61} style={{ marginRight: '12px' }} />
        )}
      </Flex>
    </Flex>
  )
}

const TicklerDisplay = ({ data }: { data: ITicklerExtended }) => {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse()

  return (
    <Flex flexDirection={'column'} width='100%'>
      <Flex flexDirection={['column', 'row', 'row']}>
        <Flex flexDirection={'column'} width={['100%', '120px', '120px']} justifyContent={'center'} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={400} fontSize='14px'>{`${data.approximateDate}`}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none', 'initial', 'initial']} />
        <Flex flexDirection={'column'} width={['100%', '180px', '180px']} justifyContent={'center'} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Tickler type</Text>
            <Text fontWeight={300} fontSize='14px'>{data.type}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none', 'initial', 'initial']} />
        <Flex flexDirection={'column'} flex={1} justifyContent={'center'} alignItems='center'>

        </Flex>
      </Flex>
    </Flex>
  )
}
interface IOption {
  label: string,
  value: 'upcoming' | 'past',
  selectedBackgroundColor: string
}
const options = [
  {
    label: 'Upcoming appointments',
    value: 'upcoming',
    selectedBackgroundColor: "#BCFFEB",
  },
  {
    label: "Past appointments",
    value: "past",
    selectedBackgroundColor: "#BCFFEB"
  },
  {
    label: 'Cases',
    value: 'cases',
    selectedBackgroundColor: "#BCFFEB",
  },
];

const optionsMobile = [
  {
    label: 'Upcoming',
    value: 'upcoming',
    selectedBackgroundColor: "#BCFFEB",
  },
  {
    label: "Past",
    value: "past",
    selectedBackgroundColor: "#BCFFEB"
  },
  {
    label: 'Cases',
    value: 'cases',
    selectedBackgroundColor: "#BCFFEB",
  },
];
interface IAppointmentExtended extends IAppointment {
  eventType: 'appointment'
}
interface ITicklerExtended extends ITickler {
  eventType: 'tickler'
}

export const Appointments: React.FC<IAppointmentsProps> = ({ id, isMinimized, defaultSelectedPeriod='upcoming' }) => {
  const [selectedPeriod, setselectedPeriod] = useState<'upcoming' | 'past' | 'cases'>(defaultSelectedPeriod);
  const [appointmentData, setAppointmentData] = useState<Array<IAppointmentExtended | ITicklerExtended> | null>(null);
  const [isParsingData, setIsParsingData] = useState<boolean>(false);

  const ref = useRef<any>();
  const isVisible = useIsVisible(ref);

  const { data: dataIntakeStatus, loading: loadingIntakeStatus } = useQuery<IGetIntakeStatusOutput, IGetIntakeStatusInput>(GET_INTAKE_STATUS, {
    variables: {
      athenaId: id
    }
  });

  const [load, { data, loading, error }] = useLazyQuery<IGetAppointmentsAndTicklersOutput, IGetAppointmentsAndTicklersInput>(GET_APPOINTMENTS_AND_TICKLERS, {
    variables: {
      patientId: id
    }
  })

  useEffect(() => {
    if (!!isVisible) {
      load()
    }
  }, [isVisible])
  
  useEffect(() => {
    if (!!loading || !selectedPeriod || !data?.providerGetAppointmentsAndTicklers) {
      setAppointmentData(null)
      return
    }

    setIsParsingData(true)

    let res: Array<IAppointmentExtended | ITicklerExtended> = []

    for (let index = 0; index < data.providerGetAppointmentsAndTicklers.appointments.length; index++) {
      const element = data.providerGetAppointmentsAndTicklers.appointments[index];
      if (element.status === 'x') continue
      const fullDate = moment(`${element.date} ${element.startTime}`, 'MM/DD/YYYY HH:mm:ss')
      let isFutureDate = moment().isBefore(fullDate)
      if (!!isFutureDate && selectedPeriod === 'upcoming') {
        res.push({
          ...element,
          eventType: 'appointment'
        })
      } else if (!isFutureDate && selectedPeriod === 'past') {
        res.push({
          ...element,
          eventType: 'appointment'
        })
      }
    }

    for (let index = 0; index < data.providerGetAppointmentsAndTicklers.ticklers.length; index++) {
      const element = data.providerGetAppointmentsAndTicklers.ticklers[index];
      let isFutureDate = moment().isBefore(element.approximateDate)
      if (!!isFutureDate && selectedPeriod === 'upcoming') {
        res.push({
          ...element,
          eventType: 'tickler'
        })
      } else if (!isFutureDate && selectedPeriod === 'past') {
        res.push({
          ...element,
          eventType: 'tickler'
        })
      }
    }

    let ordered = res.sort((a, b) => {
      let da = a.eventType === 'appointment' ? moment(`${a.date} ${a.startTime}`, 'MM/DD/YYYY HH:mm:ss') :
        moment(a.approximateDate)
      let db = b.eventType === 'appointment' ? moment(`${b.date} ${b.startTime}`, 'MM/DD/YYYY HH:mm:ss') :
        moment(b.approximateDate)

      if (moment(da).isBefore(db)) return 1
      return -1
    })
    setAppointmentData(ordered)
    setIsParsingData(false)
    return () => {

    }
  }, [data, loading, selectedPeriod]);


  return (
    <Flex ref={ref} flexDirection={'column'} width='100%' px={isMinimized ? '0' : '20px'} paddingY={'20px'}>
      <Flex flexDirection={'column'} id='rettete' style={{ width: '100%', flex: 'initial', overflow: 'hidden', height: '100%' }} paddingY={'12px'} mb={0}>
        <Flex flexDirection={['column', 'row', 'row']} alignItems={!!isMinimized ? 'baseline' : ''}>
          <Text fontWeight={600} fontSize='18px' m={'18px 28px'}>Visits</Text>
          <Flex flex={1} mt={[2, 0, 0]} justifyContent='flex-start' alignItems={'center'}>
            <Flex width={['100%']} minWidth={isMinimized ? '0' : '620px'} paddingX={['12px', 0, 0]} height={['30px', '30px', '30px']}>
              <>
                {!isMinimized ? (
                  <>
                    <SelectorLarge flex={1} minWidth={'min(640px, 100%)'} maxWidth={'min(640px, 100%)'}>
                      <SwitchSelector
                        onChange={(val) => {
                          setselectedPeriod(val as 'upcoming' | 'past' | 'cases')
                        }}
                        forcedSelectedIndex={['upcoming', 'past', 'cases'].indexOf(selectedPeriod)}
                        options={options}
                        backgroundColor={"transparent"}
                        fontColor={"#344F7A"}
                        selectedFontColor={"#344F7A"}
                        selectedBackgroundColor={"#BCFFEB"}
                      />
                    </SelectorLarge>
                    <SelectorSmall flex={1} maxWidth={'200px'}>
                      <Select
                        isSearchable={false}
                        styles={ReactSelectStyles.initialStyles}
                        options={optionsMobile}
                        value={optionsMobile[optionsMobile.findIndex(x => x.value === selectedPeriod)]}
                        onChange={(val) => {
                          setselectedPeriod(val && val.value ? val.value as any : 'past')
                        }}
                        components={{ Option }} />
                    </SelectorSmall>
                  </>
                ) : (<Select
                  styles={ReactSelectStyles.initialStyles}
                  isSearchable={false}
                  options={options as any}
                  value={options.find(x => x.value === selectedPeriod)}
                  onChange={(option: any) => {
                    if (!option) return
                    setselectedPeriod(option.value as 'upcoming' | 'past' | 'cases')
                  }} />)}
              </>
            </Flex>
          </Flex>
        </Flex>
        {selectedPeriod === 'upcoming' && dataIntakeStatus && !!dataIntakeStatus.healthCoachGetIntakeStatus && (
          <Flex justifyContent={'center'} alignItems={'center'}>

            <Flex flexDirection={'column'} style={{ gap: '12px', width: 'fit-content' }} justifyContent={'center'} alignItems={'center'}>
              {!!dataIntakeStatus.healthCoachGetIntakeStatus.notOk && (
                <Flex width={'100%'} paddingX={'16px'} paddingY={'6px'} backgroundColor={'#ff000014'} style={{ color: 'red', borderRadius: '10px', gap: '8px', border: '1px solid red', overflow:'hidden' }} alignItems={'center'}>
                  <svg width="22" height="22" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.625 105C12.7084 105 11.875 104.772 11.125 104.315C10.375 103.858 9.79169 103.253 9.37502 102.5C8.95835 101.747 8.73002 100.933 8.69002 100.06C8.65002 99.1867 8.87835 98.3333 9.37502 97.5L55.625 17.5C56.125 16.6667 56.7717 16.0417 57.565 15.625C58.3584 15.2083 59.17 15 60 15C60.83 15 61.6434 15.2083 62.44 15.625C63.2367 16.0417 63.8817 16.6667 64.375 17.5L110.625 97.5C111.125 98.3333 111.355 99.1883 111.315 100.065C111.275 100.942 111.045 101.753 110.625 102.5C110.205 103.247 109.622 103.852 108.875 104.315C108.128 104.778 107.295 105.007 106.375 105H13.625ZM22.25 95H97.75L60 30L22.25 95ZM60 90C61.4167 90 62.605 89.52 63.565 88.56C64.525 87.6 65.0034 86.4133 65 85C64.9967 83.5867 64.5167 82.4 63.56 81.44C62.6034 80.48 61.4167 80 60 80C58.5834 80 57.3967 80.48 56.44 81.44C55.4834 82.4 55.0034 83.5867 55 85C54.9967 86.4133 55.4767 87.6017 56.44 88.565C57.4034 89.5283 58.59 90.0067 60 90ZM60 75C61.4167 75 62.605 74.52 63.565 73.56C64.525 72.6 65.0034 71.4133 65 70V55C65 53.5833 64.52 52.3967 63.56 51.44C62.6 50.4833 61.4134 50.0033 60 50C58.5867 49.9967 57.4 50.4767 56.44 51.44C55.48 52.4033 55 53.59 55 55V70C55 71.4167 55.48 72.605 56.44 73.565C57.4 74.525 58.5867 75.0033 60 75Z" fill="red" />
                  </svg>

                  <Text fontWeight={300} style={{fontSize:'14px'}} >{dataIntakeStatus.healthCoachGetIntakeStatus.message || 'Intake not completed or out of date'}</Text>
                </Flex>
              )}
              {!!dataIntakeStatus.healthCoachGetIntakeStatus.notOkMeno && (
                <Flex width={'100%'} paddingX={'16px'} paddingY={'6px'} backgroundColor={'#ff000014'} style={{ color: 'red', borderRadius: '10px', gap: '8px', border: '1px solid red' }} alignItems={'center'}>
                  <svg width="22" height="22" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.625 105C12.7084 105 11.875 104.772 11.125 104.315C10.375 103.858 9.79169 103.253 9.37502 102.5C8.95835 101.747 8.73002 100.933 8.69002 100.06C8.65002 99.1867 8.87835 98.3333 9.37502 97.5L55.625 17.5C56.125 16.6667 56.7717 16.0417 57.565 15.625C58.3584 15.2083 59.17 15 60 15C60.83 15 61.6434 15.2083 62.44 15.625C63.2367 16.0417 63.8817 16.6667 64.375 17.5L110.625 97.5C111.125 98.3333 111.355 99.1883 111.315 100.065C111.275 100.942 111.045 101.753 110.625 102.5C110.205 103.247 109.622 103.852 108.875 104.315C108.128 104.778 107.295 105.007 106.375 105H13.625ZM22.25 95H97.75L60 30L22.25 95ZM60 90C61.4167 90 62.605 89.52 63.565 88.56C64.525 87.6 65.0034 86.4133 65 85C64.9967 83.5867 64.5167 82.4 63.56 81.44C62.6034 80.48 61.4167 80 60 80C58.5834 80 57.3967 80.48 56.44 81.44C55.4834 82.4 55.0034 83.5867 55 85C54.9967 86.4133 55.4767 87.6017 56.44 88.565C57.4034 89.5283 58.59 90.0067 60 90ZM60 75C61.4167 75 62.605 74.52 63.565 73.56C64.525 72.6 65.0034 71.4133 65 70V55C65 53.5833 64.52 52.3967 63.56 51.44C62.6 50.4833 61.4134 50.0033 60 50C58.5867 49.9967 57.4 50.4767 56.44 51.44C55.48 52.4033 55 53.59 55 55V70C55 71.4167 55.48 72.605 56.44 73.565C57.4 74.525 58.5867 75.0033 60 75Z" fill="red" />
                  </svg>

                  <Text fontWeight={300} style={{fontSize:'14px'}} >{dataIntakeStatus.healthCoachGetIntakeStatus.messageMeno || 'Menopause Intake not completed or out of date'}</Text>
                </Flex>
              )}
              {!!dataIntakeStatus.healthCoachGetIntakeStatus.notOkWeightloss && (
                <Flex width={'100%'} paddingX={'16px'} paddingY={'6px'} backgroundColor={'#ff000014'} style={{ color: 'red', borderRadius: '10px', gap: '8px', border: '1px solid red' }} alignItems={'center'}>
                  <svg width="22" height="22" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.625 105C12.7084 105 11.875 104.772 11.125 104.315C10.375 103.858 9.79169 103.253 9.37502 102.5C8.95835 101.747 8.73002 100.933 8.69002 100.06C8.65002 99.1867 8.87835 98.3333 9.37502 97.5L55.625 17.5C56.125 16.6667 56.7717 16.0417 57.565 15.625C58.3584 15.2083 59.17 15 60 15C60.83 15 61.6434 15.2083 62.44 15.625C63.2367 16.0417 63.8817 16.6667 64.375 17.5L110.625 97.5C111.125 98.3333 111.355 99.1883 111.315 100.065C111.275 100.942 111.045 101.753 110.625 102.5C110.205 103.247 109.622 103.852 108.875 104.315C108.128 104.778 107.295 105.007 106.375 105H13.625ZM22.25 95H97.75L60 30L22.25 95ZM60 90C61.4167 90 62.605 89.52 63.565 88.56C64.525 87.6 65.0034 86.4133 65 85C64.9967 83.5867 64.5167 82.4 63.56 81.44C62.6034 80.48 61.4167 80 60 80C58.5834 80 57.3967 80.48 56.44 81.44C55.4834 82.4 55.0034 83.5867 55 85C54.9967 86.4133 55.4767 87.6017 56.44 88.565C57.4034 89.5283 58.59 90.0067 60 90ZM60 75C61.4167 75 62.605 74.52 63.565 73.56C64.525 72.6 65.0034 71.4133 65 70V55C65 53.5833 64.52 52.3967 63.56 51.44C62.6 50.4833 61.4134 50.0033 60 50C58.5867 49.9967 57.4 50.4767 56.44 51.44C55.48 52.4033 55 53.59 55 55V70C55 71.4167 55.48 72.605 56.44 73.565C57.4 74.525 58.5867 75.0033 60 75Z" fill="red" />
                  </svg>

                  <Text fontWeight={300} style={{fontSize:'14px'}} >{dataIntakeStatus.healthCoachGetIntakeStatus.messageWeightloss || 'Weight Loss Intake not completed or out of date'}</Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        )}
        {/*  */}
        <Flex style={{ overflowY: 'hidden' }}>
          {!!loading || !!isParsingData ? (
            <ListContainer>
              <VerticalTimeline layout='1-column' lineColor='#344F7A' >
                {[0, 1, 2, 3].map(x => {
                  return (
                    <VerticalTimelineElement
                      className="vertical-timeline-element--work"
                      contentStyle={contentStyle}
                      contentArrowStyle={contentArrowStyle}
                      iconStyle={iconStyle} key={`loadingAppts${x}`}
                      style={{
                        margin: 0,
                        marginBottom: '12px'
                      }}
                    >
                      <AppointmentDisplayLoading isPast={selectedPeriod === 'past'} />
                    </VerticalTimelineElement>)
                })}
              </VerticalTimeline>
            </ListContainer>
          ) : (
            <>
              {selectedPeriod !== 'cases' ? (
                <ListContainer>
                  {appointmentData && appointmentData.length > 0 ? (
                    <>
                      <VerticalTimeline layout='1-column' lineColor='#344F7A' >
                        {(appointmentData).map(x => {
                          return (
                            <VerticalTimelineElement
                              className="vertical-timeline-element--work"
                              contentStyle={!isMinimized ? contentStyle : contentStyleMinimized}
                              contentArrowStyle={contentArrowStyle}
                              style={{
                                margin: 0,
                                marginBottom: '12px'
                              }}
                              iconStyle={iconStyle} key={x.eventType === 'appointment' ? x.id : `tickler${x.approximateDate}`}
                            >
                              {x.eventType === 'appointment' ? !isMinimized ? <AppointmentDisplay isPast={selectedPeriod === 'past'} data={x} /> : <AppointmentDisplayMinimized isPast={selectedPeriod === 'past'} data={x} /> : <TicklerDisplay data={x} />}
                            </VerticalTimelineElement>)
                        })}
                      </VerticalTimeline>
                    </>
                  ) : (
                    <Flex m={4} justifyContent='center' alignItems={'center'} width='100%'>
                      <Text textAlign='center'>No Appointments</Text>
                    </Flex>
                  )}
                </ListContainer>
              ) : (
                <Flex flex={1} id='ttt' flexDirection={'column'} paddingLeft={'12px'} width='100%' style={{ overflowY: 'hidden' }}>
                  <PatientCases patientId={id} isMinimized={isMinimized} />
                </Flex>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Flex>);
}